import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { C, card, label, btn, input } from "../lib/theme";
import PlaceField from "../lib/PlaceField";

const ANCH = ["Very inaccurate","Moderately inaccurate","Neither","Moderately accurate","Very accurate"];
const LEVELS = ["poor / low", "below average", "average", "good", "great / high"];
const IMPORTANCE = ["irrelevant", "nice but unimportant", "important", "crucial"];
const HARMONY = ["challenging", "neutral", "harmonious"];
const REL_TYPE_LABEL = { fling: "Fling", situationship: "Situationship", dating: "Dating",
  long_term: "Long-term", engagement: "Engagement", marriage: "Marriage" };
const REL_STATUS_LABEL = { together: "Still together", parted_friends: "Parted as friends",
  parted_neutral: "Parted, neutral", parted_badly: "Parted badly",
  complicated: "It's complicated", widowed: "Widowed" };
const PROMPTS = [
  "What did this relationship teach you that you'd never trade away?",
  "Which of the things you rated highest do you want to find again?",
  "What did you tolerate then that you wouldn't tolerate now?",
  "What need went unmet — and how would you name it to a future partner?",
  "What was good here that you didn't fully appreciate at the time?",
  "If a close friend described this exact relationship to you, what would you tell them?",
  "Looking across everyone you've entered so far — what pattern do you notice?",
  "What can you forgive — in them or in yourself — now that it's written down?",
];

export default function Assessments() {
  const [instruments, setInstruments] = useState([]);
  const [taking, setTaking] = useState(null);
  const [resp, setResp] = useState({});
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const [past, setPast] = useState([]);
  const [chart, setChart] = useState(null);
  const [err, setErr] = useState("");
  const [partners, setPartners] = useState([]);
  const [attrCatalog, setAttrCatalog] = useState(null);
  const [addingPartner, setAddingPartner] = useState(false);

  useEffect(() => {
    api.instruments().then(setInstruments);
    api.assessments().then(setPast);
    api.charts().then((cs) => setChart(cs.find((c) => c.is_self)));
    api.partnerAttributes().then(setAttrCatalog).catch(() => {});
    api.partners().then(setPartners).catch(() => {});
  }, []);

  if (result) return (
    <div style={{ maxWidth: 640 }}>
      <div style={card}>
        <div style={label}>RESULTS</div>
        {result.scores.domains
          ? Object.entries(result.scores.domains).map(([d, v]) => (
              <Bar key={d} name={d} v={v} max={100} facets={result.scores.facets[d]} />))
          : result.scores.display
          ? Object.entries(result.scores.display).map(([d, v]) => (
              <Bar key={d} name={d} v={v} max={100} />))
          : Object.entries(result.scores).map(([d, v]) => (
              <Bar key={d} name={d} v={(v - 10) * 2.5} max={100} raw={v} />))}
        {result.badges_awarded.length > 0 && (
          <div style={{ color: C.brass, fontSize: 12.5, marginTop: 10 }}>
            Seal earned: {result.badges_awarded.join(", ")}</div>)}
        <button style={{ ...btn(false), marginTop: 12 }}
          onClick={() => { setResult(null); setTaking(null); api.assessments().then(setPast); }}>
          Done</button>
      </div>
    </div>
  );

  if (taking) {
    const items = taking.items.slice(page * 10, page * 10 + 10);
    const answered = Object.keys(resp).length;
    const done = answered === taking.items.length;
    return (
      <div style={{ maxWidth: 640 }}>
        <div style={{ fontSize: 11, color: C.faint, marginBottom: 8 }}>
          {taking.title} · {answered}/{taking.items.length}</div>
        <div style={{ ...card, padding: "8px 14px", marginBottom: 8, fontSize: 11.5,
          color: C.dim, display: "flex", flexWrap: "wrap", gap: "2px 14px" }}>
          <span style={{ color: C.faint }}>How accurately does each statement describe you?</span>
          {ANCH.map((a, i) => (
            <span key={a} style={{ whiteSpace: "nowrap" }}>
              <b style={{ color: C.brass }}>{i + 1}</b> {a}</span>))}
        </div>
        {items.map((it) => (
          <div key={it.id} style={{ ...card, padding: "10px 14px", marginBottom: 6 }}>
            <div style={{ fontSize: 13, marginBottom: 6 }}>{it.text}</div>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <span style={{ fontSize: 10, color: C.dim, paddingRight: 3 }}>inaccurate</span>
              {[1,2,3,4,5].map((v) => (
                <button key={v} title={ANCH[v-1]}
                  onClick={() => setResp({ ...resp, [it.id]: v })}
                  style={{ width: 32, height: 28, borderRadius: 4, cursor: "pointer",
                    border: `1px solid ${resp[it.id] === v ? C.brass : C.line}`,
                    background: resp[it.id] === v ? C.brass : C.surface2,
                    color: resp[it.id] === v ? C.bg : C.dim, fontFamily: "inherit" }}>{v}</button>))}
              <span style={{ fontSize: 10, color: C.dim, paddingLeft: 3 }}>accurate</span>
              {resp[it.id] && <span style={{ fontSize: 10.5, color: C.brass, marginLeft: 6 }}>
                {ANCH[resp[it.id] - 1]}</span>}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
          <button style={btn(false)} disabled={page === 0} onClick={() => setPage(page - 1)}>Back</button>
          {page < Math.ceil(taking.items.length / 10) - 1
            ? <button style={btn()} onClick={() => setPage(page + 1)}>Next</button>
            : <button style={btn()} disabled={!done} onClick={() =>
                api.submitAssessment({ chart_id: chart.id, instrument: taking.id, responses: resp })
                  .then(setResult).catch((e) => setErr(e.message))}>Submit</button>}
        </div>
        {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 8 }}>{err}</div>}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={label}>THE STUDY</div>
        <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.65 }}>
          This project is built around a simple, testable question: do the placements of a
          birth chart correspond to measurable personality traits? These assessments are the
          scientific half of that experiment. The Big Five is psychology's most validated
          model of personality — by setting your scores beside your chart's placements, and
          doing the same across many members, we can look for patterns honestly instead of
          assuming them.
        </div>
        <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.6, marginTop: 8 }}>
          Your data is never sold and is used for this research only. Scores enter the
          study anonymously and only in aggregate, only if you opted into research — you
          can change that anytime in your Profile.
        </div>
      </div>
      {!chart && <div style={{ ...card, marginBottom: 12, fontSize: 12.5, color: C.dim }}>
        Create your chart first — assessments bind to your own chart.</div>}
      {instruments.map((i) => (
        <div key={i.id} style={{ ...card, marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{i.title}</div>
          <div style={{ fontSize: 11.5, color: C.dim, margin: "4px 0 10px" }}>
            {i.items.length} items · {i.license}</div>
          <button style={btn()} disabled={!chart}
            onClick={() => { setTaking(i); setResp({}); setPage(0); }}>Begin</button>
        </div>
      ))}
      {past.length > 0 && <>
        <div style={label}>YOUR ASSESSMENTS</div>
        {past.map((a) => (
          <div key={a.id} style={{ fontSize: 12, color: C.dim, padding: "4px 0",
            borderBottom: `1px solid ${C.line}` }}>
            {a.instrument} · {String(a.completed_at).slice(0, 10)}</div>))}
      </>}

      <div style={{ ...card, marginTop: 18, marginBottom: 12 }}>
        <div style={label}>RELATIONSHIPS · SYNASTRY</div>
        <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.65 }}>
          The other half of the study. Enter the charts of current and past partners —
          initials only — and rate what they were like and how it fit you. Reviewing a
          relationship honestly is worth doing for its own sake; it also lets us ask
          whether the traits people bring to relationships track their charts.
        </div>
        <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.6, marginTop: 8 }}>
          Birth details and notes are encrypted; entries are never visible to anyone
          else and never appear in community features. Use initials or a nickname,
          never a full name.
        </div>
        {partners.length > 0 && <div style={{ marginTop: 12 }}>
          {partners.map((p) => (
            <div key={p.id} style={{ display: "flex", alignItems: "baseline", gap: 8,
              borderTop: `1px solid ${C.line}`, padding: "7px 0", fontSize: 12.5 }}>
              <b>{p.label}</b>
              <span style={{ color: C.dim }}>
                {REL_TYPE_LABEL[p.rel_type]} · {REL_STATUS_LABEL[p.rel_status]}
                {p.rel_years != null && ` · ${p.rel_years} yr`}
                {" · "}{Object.keys(p.ratings).length} traits rated
                {!p.time_known && " · no birth time"}</span>
              <button onClick={() => { if (confirm(`Delete the entry for ${p.label}?`))
                  api.deletePartner(p.id).then(() => api.partners().then(setPartners)); }}
                style={{ all: "unset", cursor: "pointer", color: C.faint, fontSize: 11,
                  marginLeft: "auto" }}>delete</button>
            </div>))}
        </div>}
        {!addingPartner && attrCatalog &&
          <button style={{ ...btn(), marginTop: 10 }} onClick={() => setAddingPartner(true)}>
            Add a partner</button>}
      </div>
      {addingPartner && attrCatalog && (
        <PartnerWizard catalog={attrCatalog} count={partners.length}
          onDone={() => { setAddingPartner(false); api.partners().then(setPartners); }}
          onCancel={() => setAddingPartner(false)} />
      )}
    </div>
  );
}

function PartnerWizard({ catalog, count, onDone, onCancel }) {
  const [step, setStep] = useState(0);
  const [p, setP] = useState({ label: "", date: "", time: "", timeKnown: false,
    lat: "", lon: "", rel_type: "dating", rel_status: "parted_neutral",
    rel_years: "", satisfaction: "", note: "" });
  const [ratings, setRatings] = useState({});
  const [depth, setDepth] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  const byCategory = {};
  catalog.attributes.forEach((a) => (byCategory[a.category] =
    [...(byCategory[a.category] || []), a]));
  const setR = (slug, patch) => setRatings((rs) => {
    const cur = rs[slug] || {};
    return { ...rs, [slug]: { ...cur, ...patch } };
  });
  const rated = Object.values(ratings).filter((r) => r.level).length;

  const submit = () => {
    setBusy(true); setErr("");
    const clean = {};
    Object.entries(ratings).forEach(([slug, r]) => {
      if (r.level) clean[slug] = { level: r.level,
        importance: r.importance || null, harmony: r.harmony || null };
    });
    api.createPartner({
      label: p.label.trim(),
      birth_local: p.timeKnown && p.time ? `${p.date}T${p.time}` : p.date,
      time_known: p.timeKnown && !!p.time,
      lat: p.lat === "" ? null : Number(p.lat),
      lon: p.lon === "" ? null : Number(p.lon),
      rel_type: p.rel_type, rel_status: p.rel_status,
      rel_years: p.rel_years === "" ? null : Number(p.rel_years),
      satisfaction: p.satisfaction === "" ? null : Number(p.satisfaction),
      note: p.note.trim() || null, ratings: clean,
    }).then(() => setSaved(true)).catch((e) => setErr(e.message))
      .finally(() => setBusy(false));
  };

  if (saved) return (
    <div style={{ ...card, marginBottom: 12 }}>
      <div style={label}>SAVED</div>
      <div style={{ fontSize: 14, lineHeight: 1.7, margin: "8px 0 4px", color: C.text }}>
        {PROMPTS[count % PROMPTS.length]}
      </div>
      <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 12 }}>
        Nothing to type — just worth sitting with for a moment.
      </div>
      <button style={btn()} onClick={onDone}>Done</button>
    </div>
  );

  return (
    <div style={{ ...card, marginBottom: 12 }}>
      <div style={label}>{`ADD A PARTNER · STEP ${step + 1} OF 3`}</div>
      {step === 0 && <>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0 6px" }}>
          <div>
            <Fl>Initials / nickname</Fl>
            <input value={p.label} placeholder="e.g. J.R." maxLength={24}
              onChange={(e) => setP((x) => ({ ...x, label: e.target.value }))}
              style={{ ...input, width: 110 }} />
          </div>
          <div>
            <Fl>Birth date</Fl>
            <input type="date" value={p.date}
              onChange={(e) => setP((x) => ({ ...x, date: e.target.value }))} style={input} />
          </div>
          <div>
            <Fl>Birth time</Fl>
            <input type="time" value={p.time} disabled={!p.timeKnown}
              onChange={(e) => setP((x) => ({ ...x, time: e.target.value }))}
              style={{ ...input, opacity: p.timeKnown ? 1 : 0.45 }} />
          </div>
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer",
          fontSize: 12, color: C.dim, marginBottom: 8 }}>
          <input type="checkbox" checked={p.timeKnown}
            onChange={(e) => setP((x) => ({ ...x, timeKnown: e.target.checked }))}
            style={{ accentColor: C.brass }} />
          <span>I know their birth time (unlocks Ascendant, houses, and lots — without
            it we store placements only)</span>
        </label>
        <Fl>Birthplace (optional — improves accuracy, needed for houses)</Fl>
        <PlaceField onResolve={({ lat, lon }) => setP((x) => ({ ...x, lat, lon }))} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          <div>
            <Fl>Relationship</Fl>
            <select value={p.rel_type}
              onChange={(e) => setP((x) => ({ ...x, rel_type: e.target.value }))} style={input}>
              {catalog.rel_types.map((t) => <option key={t} value={t}>{REL_TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          <div>
            <Fl>How it stands</Fl>
            <select value={p.rel_status}
              onChange={(e) => setP((x) => ({ ...x, rel_status: e.target.value }))} style={input}>
              {catalog.rel_statuses.map((s) => <option key={s} value={s}>{REL_STATUS_LABEL[s]}</option>)}
            </select>
          </div>
          <div>
            <Fl>Years together</Fl>
            <input value={p.rel_years} placeholder="e.g. 2.5" inputMode="decimal"
              onChange={(e) => setP((x) => ({ ...x, rel_years: e.target.value }))}
              style={{ ...input, width: 80 }} />
          </div>
          <div>
            <Fl>Overall satisfaction (1–10)</Fl>
            <input value={p.satisfaction} placeholder="1–10" inputMode="numeric"
              onChange={(e) => setP((x) => ({ ...x, satisfaction: e.target.value }))}
              style={{ ...input, width: 70 }} />
          </div>
        </div>
        <Nav2>
          <button style={btn(false)} onClick={onCancel}>Cancel</button>
          <button style={btn()} disabled={!p.label.trim() || !p.date}
            onClick={() => setStep(1)}>Continue</button>
        </Nav2>
      </>}

      {step === 1 && <>
        <div style={{ fontSize: 12, color: C.dim, margin: "8px 0" }}>
          Rate what they were like. Skip anything that doesn't apply —
          <b> {rated}</b> rated so far.
        </div>
        <div style={{ fontSize: 11, color: C.faint, marginBottom: 8 }}>
          1 {LEVELS[0]} · 3 {LEVELS[2]} · 5 {LEVELS[4]}
          <button onClick={() => setDepth(!depth)} style={{ all: "unset", cursor: "pointer",
            color: C.brass, marginLeft: 10 }}>
            {depth ? "hide" : "also rate"} importance &amp; fit</button>
        </div>
        {Object.entries(byCategory).map(([cat, attrs]) => (
          <div key={cat} style={{ marginBottom: 10 }}>
            <div style={{ ...label, fontSize: 10 }}>{cat.toUpperCase()}</div>
            {attrs.map((a) => (
              <div key={a.slug} style={{ padding: "5px 0", borderBottom: `1px solid ${C.line}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12.5, minWidth: 200 }}>{a.label}</span>
                  <span style={{ display: "flex", gap: 4 }}>
                    {[1,2,3,4,5].map((v) => (
                      <button key={v} title={LEVELS[v-1]}
                        onClick={() => setR(a.slug, { level: ratings[a.slug]?.level === v ? undefined : v })}
                        style={{ width: 28, height: 24, borderRadius: 4, cursor: "pointer",
                          border: `1px solid ${ratings[a.slug]?.level === v ? C.brass : C.line}`,
                          background: ratings[a.slug]?.level === v ? C.brass : C.surface2,
                          color: ratings[a.slug]?.level === v ? C.bg : C.dim,
                          fontFamily: "inherit", fontSize: 11.5 }}>{v}</button>))}
                  </span>
                  {depth && ratings[a.slug]?.level && <>
                    <select value={ratings[a.slug]?.importance || ""}
                      onChange={(e) => setR(a.slug, { importance: Number(e.target.value) || undefined })}
                      style={{ ...input, fontSize: 11, padding: "3px 6px" }}>
                      <option value="">importance…</option>
                      {IMPORTANCE.map((l, i) => <option key={l} value={i+1}>{l}</option>)}
                    </select>
                    <select value={ratings[a.slug]?.harmony || ""}
                      onChange={(e) => setR(a.slug, { harmony: Number(e.target.value) || undefined })}
                      style={{ ...input, fontSize: 11, padding: "3px 6px" }}>
                      <option value="">fit…</option>
                      {HARMONY.map((l, i) => <option key={l} value={i+1}>{l}</option>)}
                    </select>
                  </>}
                </div>
              </div>))}
          </div>))}
        <Nav2>
          <button style={btn(false)} onClick={() => setStep(0)}>Back</button>
          <button style={btn()} disabled={rated === 0} onClick={() => setStep(2)}>Continue</button>
        </Nav2>
      </>}

      {step === 2 && <>
        <div style={{ fontSize: 12, color: C.dim, margin: "8px 0" }}>
          Anything else worth setting down about this relationship? Private and
          encrypted, like your calendar notes.
        </div>
        <textarea value={p.note} rows={4}
          onChange={(e) => setP((x) => ({ ...x, note: e.target.value }))}
          placeholder="What was good, what was hard, what you'd want next time…"
          style={{ ...input, width: "100%", boxSizing: "border-box", resize: "vertical",
            fontFamily: "inherit" }} />
        <Nav2>
          <button style={btn(false)} disabled={busy} onClick={() => setStep(1)}>Back</button>
          <button style={btn()} disabled={busy} onClick={submit}>
            {busy ? "Saving…" : "Save entry"}</button>
        </Nav2>
        {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 8 }}>{err}</div>}
      </>}
    </div>
  );
}

const Fl = ({ children }) => (
  <div style={{ fontSize: 10, letterSpacing: "0.08em", color: C.faint,
    marginBottom: 4 }}>{children}</div>);
const Nav2 = ({ children }) => (
  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
    {children}</div>);

function Bar({ name, v, max, raw, facets }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
        <span>{name}</span>
        <span style={{ fontFamily: "ui-monospace,monospace", color: C.brass }}>
          {raw ?? Math.round(v)}</span>
      </div>
      <div style={{ position: "relative", height: 10, background: C.surface2, borderRadius: 5, margin: "3px 0" }}>
        <div style={{ position: "absolute", inset: "0 auto 0 0", width: `${(v / max) * 100}%`,
          background: C.brass, borderRadius: 5, opacity: 0.85 }} />
        <div style={{ position: "absolute", left: "50%", top: -2, bottom: -2, width: 1, background: C.faint }} />
      </div>
      {facets && <div style={{ fontSize: 10.5, color: C.dim }}>
        {Object.entries(facets).map(([f, fv]) => `${f} ${Math.round(fv)}`).join(" · ")}</div>}
    </div>
  );
}
